import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AdminSidebar from '../components/AdminSidebar';
import { getDashboardStats } from '../utils/api';
import { Package, TrendingUp, DollarSign, Clock } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await getDashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-[#0F1219] items-center justify-center">
        <div className="text-primary font-mono text-xl">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#0F1219]">
      <div className="scanline-overlay" />
      <AdminSidebar />

      <div className="flex-1 ml-64 p-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-heading font-bold text-white mb-2 tracking-wider" data-testid="dashboard-title">DASHBOARD</h1>
          <p className="text-gray-400">System overview and key metrics</p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<Package />}
            label="Available Stock"
            value={`${stats?.available_k || 0}K`}
            color="primary"
            delay={0.1}
            testId="stat-available-stock"
          />
          <StatCard
            icon={<Clock />}
            label="Reserved Stock"
            value={`${stats?.reserved_k || 0}K`}
            color="accent"
            delay={0.2}
            testId="stat-reserved-stock"
          />
          <StatCard
            icon={<TrendingUp />}
            label="Total Sold"
            value={`${stats?.total_sold_k || 0}K`}
            color="primary"
            delay={0.3}
            testId="stat-total-sold"
          />
          <StatCard
            icon={<DollarSign />}
            label="Revenue"
            value={`₹${stats?.revenue?.toFixed(2) || 0}`}
            color="primary"
            delay={0.4}
            testId="stat-revenue"
          />
        </div>

        {/* Pending Orders Alert */}
        {stats && stats.pending_orders > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-accent/10 border border-accent/30 p-6 mb-8"
            data-testid="pending-orders-alert"
          >
            <div className="flex items-center gap-4">
              <div className="w-3 h-3 bg-accent animate-pulse" />
              <div>
                <h3 className="text-accent font-heading font-bold text-xl tracking-wider">
                  {stats.pending_orders} PENDING ORDER{stats.pending_orders !== 1 ? 'S' : ''}
                </h3>
                <p className="text-gray-400 text-sm">Review and process pending orders in the Orders section</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-card border border-border/50 p-8"
        >
          <h2 className="text-2xl font-heading font-bold text-white mb-6 tracking-wider">SYSTEM STATUS</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <StatusItem label="API" status="ONLINE" />
            <StatusItem label="Database" status="ONLINE" />
            <StatusItem label="Notifications" status="ACTIVE" />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, color, delay, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    whileHover={{ y: -4 }}
    className="relative bg-card border border-border/50 p-6 group hover:border-primary/50 transition-colors"
    data-testid={testId}
  >
    <div className="absolute top-0 right-0 w-2 h-2 bg-primary" />
    <div className={`text-${color} mb-4`}>{icon}</div>
    <div className="text-gray-400 text-sm font-heading tracking-wider uppercase mb-2">{label}</div>
    <div className="text-3xl font-mono font-bold text-primary">{value}</div>
  </motion.div>
);

const StatusItem = ({ label, status }) => (
  <div className="flex items-center justify-between p-4 bg-black/40 border border-primary/20" data-testid={`status-${label.toLowerCase()}`}>
    <span className="text-gray-400 font-mono uppercase tracking-wider text-sm">{label}</span>
    <div className="flex items-center gap-2">
      <div className="w-2 h-2 bg-primary animate-pulse" />
      <span className="text-primary font-mono font-bold text-sm">{status}</span>
    </div>
  </div>
);

export default AdminDashboard;